import { useQuery, userIdFromQueryParams } from '@util/QueryParams';
import { Avatar, Button } from 'antd';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'ts-utils';
import { Color } from '../../../config/Color';
import { AppSelector, useAppStore } from '../../../store/Store';
import { UserState } from '../../../store/user/UserState';
import { appOverride } from '../../../util/AppUtils';
import * as icons from '../../assets/images/Icons';
import { AppLogos } from '../../assets/images/Logos';
import { PrivacyChoicesButton } from '../../ConsentPreferences';
import { useFlowNavigator } from '../../flows/FlowNavigation';
import { Body } from '../../styles/Typography';
import { Coupon } from './Coupon';
import { CurrencySelect } from './CurrencySelect';

export type HeaderProps = {
  loading: boolean;
  user: UserState;
  /** The redesigned page shows the discount in its checkout card instead of a banner. */
  hideCoupon?: boolean;
};

export const Header: FunctionComponent<HeaderProps> = (props) => {
  const { name, email = '' } = props.user;

  return (
    <>
      {props.hideCoupon === true || <Coupon />}
      <TopNavBar>
        <BackButton />
        <StyledPrivacyChoicesButton />
      </TopNavBar>
      {!isEmpty(email) && (
        <HeaderContainer>
          <UserHeader name={name} email={email} />
        </HeaderContainer>
      )}

      <LogoSection>
        <Logo src={AppLogos.header} alt="Logo" data-test="appLogo" />
        <CurrencySelect loading={props.loading} />
      </LogoSection>
    </>
  );
};

const BackButton: FunctionComponent = () => {
  const pageStack = useAppStore(AppSelector.pageStack);
  const canGoBack = pageStack.length > 1;
  const navigation = useFlowNavigator();

  if (!canGoBack) {
    return <BackButtonPlaceholder />;
  }

  return (
    <StyledBackButton
      type="link"
      onClick={navigation.goBack}
      icon={<BackIcon src={icons.backBlack} alt="Back" />}
    />
  );
};

type UserHeaderProps = { name?: string; email: string };

const UserHeader: FunctionComponent<UserHeaderProps> = ({ name, email }) => {
  const query = useQuery();

  const userIdFromQueryString = userIdFromQueryParams(query);
  const hideEmail = userIdFromQueryString !== undefined;

  return (
    <>
      <div>
        <UserAvatar>{(!isEmpty(name) ? name : email).slice(0, 1).toUpperCase()}</UserAvatar>
      </div>
      <HeaderEmail>{hideEmail ? '' : email}</HeaderEmail>
    </>
  );
};

const TopNavBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing._12px} ${theme.spacing._20px} 0`};
  background: ${appOverride({ nerva: 'transparent', default: Color.surface.default })};
`;

const StyledPrivacyChoicesButton = styled(PrivacyChoicesButton)`
  appearance: none;
  border: 0;
  background: transparent;
  color: ${appOverride({ nerva: Color.content.default, default: Color.action.default })};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.xxxs};
  padding: 0;
  text-decoration: underline;
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing._12px} ${theme.spacing._20px}`};
  background: ${appOverride({ nerva: 'transparent', default: Color.surface.default })};
`;

const Logo = styled.img`
  height: ${({ theme }) => theme.spacing._30px};
`;

const BackIcon = styled.img`
  filter: brightness(0) invert(1);
`;

const StyledBackButton = styled(Button)`
  padding: ${({ theme }) => `${theme.spacing._6px} ${theme.spacing._6px} ${theme.spacing._6px} 0`};
  display: inline-flex;
  align-items: center;
  height: ${({ theme }) => theme.spacing._28px};
  min-width: 40px;
`;

const BackButtonPlaceholder = styled.div`
  width: 40px;
  height: ${({ theme }) => theme.spacing._28px};
`;

export const HeaderContainer = styled.div<{ hide?: boolean }>`
  background: ${(props) =>
    appOverride({
      nerva: 'transparent',
      default: props.hide ? Color.surface.accent : Color.surface.muted,
    })};
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.hide ? 'space-between' : 'flex-end')};
  padding: ${({ theme }) => `${theme.spacing._10px} ${theme.spacing._12px}`};
  align-items: center;
`;

const UserAvatar = styled(Avatar)`
  font-family: Circular, Helvetica, Verdana, sans-serif;
  background-color: ${appOverride({ nerva: Color.flow.accent, default: 'white' })};
  color: ${appOverride({ nerva: 'transparent', default: 'black' })};

  ${appOverride({
    nerva: `
    .ant-avatar-string {
      background: ${Color.action.default};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  `,
    default: '',
  })}
`;

const HeaderEmail = styled(Body)`
  font-size: ${({ theme }) => theme.fontSize.xxs};
  color: ${Color.content.default};
  margin-bottom: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  margin: ${({ theme }) => `0 ${theme.spacing._10px} 0 ${theme.spacing._10px}`};
`;
